import * as api from "../api/index.js";
import { SNACKBAR_DURATION_MS, getSnackbarColor } from "../utils/snackbar";

// Action types
export const SUBSCRIPTION_ACTIONS = {
  // Package selection
  SET_SELECTED_PACKAGE: "SET_SELECTED_PACKAGE",
  SET_USER_COUNT: "SET_USER_COUNT",
  SET_BILLING_TYPE: "SET_BILLING_TYPE",

  // Discount codes
  SET_DISCOUNT_CODE: "SET_DISCOUNT_CODE",
  SET_APPLIED_DISCOUNT: "SET_APPLIED_DISCOUNT",
  VALIDATE_DISCOUNT_START: "VALIDATE_DISCOUNT_START",
  VALIDATE_DISCOUNT_SUCCESS: "VALIDATE_DISCOUNT_SUCCESS",
  VALIDATE_DISCOUNT_ERROR: "VALIDATE_DISCOUNT_ERROR",
  CLEAR_DISCOUNT: "CLEAR_DISCOUNT",

  // Pricing calculations
  SET_CALCULATED_PRICING: "SET_CALCULATED_PRICING",
  CALCULATE_PRICING: "CALCULATE_PRICING",

  // Registration flow
  SET_REGISTRATION_DATA: "SET_REGISTRATION_DATA",
  CLEAR_REGISTRATION_DATA: "CLEAR_REGISTRATION_DATA",

  // Subscription flow
  START_SUBSCRIPTION_FLOW: "START_SUBSCRIPTION_FLOW",
  COMPLETE_SUBSCRIPTION_FLOW: "COMPLETE_SUBSCRIPTION_FLOW",
  CLEAR_SUBSCRIPTION_DATA: "CLEAR_SUBSCRIPTION_DATA",

  // Loading states
  SET_LOADING: "SET_SUBSCRIPTION_LOADING",
  SET_ERROR: "SET_SUBSCRIPTION_ERROR",
  CLEAR_ERROR: "CLEAR_SUBSCRIPTION_ERROR",

  // My Subscription Management
  FETCH_MY_SUBSCRIPTION_START: "FETCH_MY_SUBSCRIPTION_START",
  FETCH_MY_SUBSCRIPTION_SUCCESS: "FETCH_MY_SUBSCRIPTION_SUCCESS",
  FETCH_MY_SUBSCRIPTION_ERROR: "FETCH_MY_SUBSCRIPTION_ERROR",

  CANCEL_SUBSCRIPTION_START: "CANCEL_SUBSCRIPTION_START",
  CANCEL_SUBSCRIPTION_SUCCESS: "CANCEL_SUBSCRIPTION_SUCCESS",
  CANCEL_SUBSCRIPTION_ERROR: "CANCEL_SUBSCRIPTION_ERROR",

  // Billing History
  FETCH_BILLING_HISTORY_START: "FETCH_BILLING_HISTORY_START",
  FETCH_BILLING_HISTORY_SUCCESS: "FETCH_BILLING_HISTORY_SUCCESS",
  FETCH_BILLING_HISTORY_ERROR: "FETCH_BILLING_HISTORY_ERROR",

  // Plan Management Actions (following project pattern)
  FETCH_AVAILABLE_PLANS_START: "FETCH_AVAILABLE_PLANS_START",
  FETCH_AVAILABLE_PLANS_SUCCESS: "FETCH_AVAILABLE_PLANS_SUCCESS",
  FETCH_AVAILABLE_PLANS_ERROR: "FETCH_AVAILABLE_PLANS_ERROR",

  CHANGE_PLAN_START: "CHANGE_PLAN_START",
  CHANGE_PLAN_SUCCESS: "CHANGE_PLAN_SUCCESS",
  CHANGE_PLAN_ERROR: "CHANGE_PLAN_ERROR",

  UPDATE_USER_COUNT_START: "UPDATE_USER_COUNT_START",
  UPDATE_USER_COUNT_SUCCESS: "UPDATE_USER_COUNT_SUCCESS",
  UPDATE_USER_COUNT_ERROR: "UPDATE_USER_COUNT_ERROR",

  PREVIEW_PLAN_CHANGE_START: "PREVIEW_PLAN_CHANGE_START",
  PREVIEW_PLAN_CHANGE_SUCCESS: "PREVIEW_PLAN_CHANGE_SUCCESS",
  PREVIEW_PLAN_CHANGE_ERROR: "PREVIEW_PLAN_CHANGE_ERROR",

  // Subscription Status Check
  CHECK_SUBSCRIPTION_STATUS_START: "CHECK_SUBSCRIPTION_STATUS_START",
  CHECK_SUBSCRIPTION_STATUS_SUCCESS: "CHECK_SUBSCRIPTION_STATUS_SUCCESS",
  CHECK_SUBSCRIPTION_STATUS_ERROR: "CHECK_SUBSCRIPTION_STATUS_ERROR",
  RESET_SUBSCRIPTION_STATUS: "RESET_SUBSCRIPTION_STATUS",
};

// Action creators - Package Selection
export const setSelectedPackage = (packageData) => ({
  type: SUBSCRIPTION_ACTIONS.SET_SELECTED_PACKAGE,
  payload: packageData,
});

export const setUserCount = (count) => ({
  type: SUBSCRIPTION_ACTIONS.SET_USER_COUNT,
  payload: Math.max(1, parseInt(count) || 1),
});

export const setBillingType = (billingType) => ({
  type: SUBSCRIPTION_ACTIONS.SET_BILLING_TYPE,
  payload: billingType,
});

// Action creators - Discount Codes
export const setDiscountCode = (code) => ({
  type: SUBSCRIPTION_ACTIONS.SET_DISCOUNT_CODE,
  payload: code,
});

export const setAppliedDiscount = (discount) => ({
  type: SUBSCRIPTION_ACTIONS.SET_APPLIED_DISCOUNT,
  payload: discount,
});

export const clearDiscount = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_DISCOUNT,
});

// Async action - Validate discount code
export const validateDiscountCode = (codeData) => async (dispatch) => {
  try {
    dispatch({ type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_START });

    const response = await api.validateDiscountCode(codeData);

    if (response.data.valid) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_SUCCESS,
        payload: response.data.discount,
      });
      dispatch(setAppliedDiscount(response.data.discount));
      return { success: true, discount: response.data.discount };
    } else {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_ERROR,
        payload: response.data.message,
      });
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    const message =
      error.response?.data?.message || "Failed to validate discount code";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

// Action creators - Pricing
export const setCalculatedPricing = (pricing) => ({
  type: SUBSCRIPTION_ACTIONS.SET_CALCULATED_PRICING,
  payload: pricing,
});

export const calculatePricing = () => (dispatch, getState) => {
  const { subscription } = getState();
  const { selectedPackage, userCount, billingType, appliedDiscount } =
    subscription;

  if (!selectedPackage) {
    return dispatch(
      setCalculatedPricing({ subtotal: 0, discount: 0, total: 0 })
    );
  }

  // Determine which pricing field to use and model
  let basePrice;
  let isPerUser = false;

  // For the enhanced subscription flow, treat all packages as per-user by default
  // This allows existing packages to work with the new user count selector

  if (selectedPackage.basePricePerMonth || selectedPackage.basePricePerYear) {
    // Package has explicit per-user pricing fields
    basePrice =
      billingType === "yearly"
        ? selectedPackage.basePricePerYear || 0
        : selectedPackage.basePricePerMonth || 0;
    isPerUser = true;
  } else {
    // Convert flat pricing to per-user pricing by dividing by a reasonable user count
    // This makes existing packages work with the new user selector
    const flatPrice =
      billingType === "yearly"
        ? selectedPackage.basePricePerYear || 0
        : selectedPackage.basePricePerMonth || 0;

    // Divide by 5 to get a per-user price (assuming packages were priced for ~5 users)
    basePrice = flatPrice / 5;
    isPerUser = true;
  }

  console.log("🔧 Pricing calculation:", {
    packageName: selectedPackage.packageName,
    basePrice,
    isPerUser,
    userCount,
    billingType,
    pricingModel: selectedPackage.pricingModel,
  });

  let subtotal = basePrice * (isPerUser ? userCount : 1);

  // Apply volume discounts
  if (selectedPackage.volumeDiscounts && isPerUser) {
    const applicableDiscount = selectedPackage.volumeDiscounts
      .filter((vd) => userCount >= vd.minUsers)
      .sort((a, b) => b.minUsers - a.minUsers)[0];

    if (applicableDiscount) {
      subtotal = subtotal * (1 - applicableDiscount.discountPercentage / 100);
    }
  }

  // Apply discount code
  let discountAmount = 0;
  if (appliedDiscount) {
    if (appliedDiscount.type === "percentage") {
      discountAmount = subtotal * (appliedDiscount.value / 100);
    } else {
      discountAmount = appliedDiscount.value;
    }

    // Apply maximum discount if set
    if (
      appliedDiscount.maximumDiscount &&
      discountAmount > appliedDiscount.maximumDiscount
    ) {
      discountAmount = appliedDiscount.maximumDiscount;
    }
  }

  const total = Math.max(0, subtotal - discountAmount);

  const pricing = {
    subtotal: Math.round(subtotal * 100) / 100,
    discount: Math.round(discountAmount * 100) / 100,
    total: Math.round(total * 100) / 100,
    basePrice,
    userCount: selectedPackage.pricingModel === "per_user" ? userCount : 1,
  };

  dispatch(setCalculatedPricing(pricing));
  return pricing;
};

// Action creators - Registration Flow
export const setRegistrationData = (data) => ({
  type: SUBSCRIPTION_ACTIONS.SET_REGISTRATION_DATA,
  payload: data,
});

export const clearRegistrationData = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_REGISTRATION_DATA,
});

// Action creators - Subscription Flow
export const startSubscriptionFlow = (packageData) => (dispatch) => {
  dispatch({
    type: SUBSCRIPTION_ACTIONS.START_SUBSCRIPTION_FLOW,
    payload: packageData,
  });
  dispatch(setSelectedPackage(packageData));
};

export const completeSubscriptionFlow = (subscriptionData) => ({
  type: SUBSCRIPTION_ACTIONS.COMPLETE_SUBSCRIPTION_FLOW,
  payload: subscriptionData,
});

export const clearSubscriptionData = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_SUBSCRIPTION_DATA,
});

// Action creators - UI State
export const setLoading = (isLoading) => ({
  type: SUBSCRIPTION_ACTIONS.SET_LOADING,
  payload: isLoading,
});

export const setError = (error) => ({
  type: SUBSCRIPTION_ACTIONS.SET_ERROR,
  payload: error,
});

export const clearError = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_ERROR,
});

// Utility actions
export const initializeSubscriptionFlow =
  (packageData, userCount = 1, billingType = "monthly") =>
  (dispatch) => {
    dispatch(setSelectedPackage(packageData));
    dispatch(setUserCount(userCount));
    dispatch(setBillingType(billingType));
    dispatch(calculatePricing());
  };

export const updateUserCountAndRecalculate = (count) => (dispatch) => {
  dispatch(setUserCount(count));
  dispatch(calculatePricing());
};

export const updateBillingTypeAndRecalculate = (billingType) => (dispatch) => {
  dispatch(setBillingType(billingType));
  dispatch(calculatePricing());
};

// Persistence actions (localStorage)
export const saveToLocalStorage = () => (dispatch, getState) => {
  const { subscription } = getState();
  try {
    localStorage.setItem(
      "quantum_subscription_data",
      JSON.stringify({
        selectedPackage: subscription.selectedPackage,
        userCount: subscription.userCount,
        billingType: subscription.billingType,
        appliedDiscount: subscription.appliedDiscount,
        calculatedPricing: subscription.calculatedPricing,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.warn("Failed to save subscription data to localStorage:", error);
  }
};

export const loadFromLocalStorage = () => (dispatch) => {
  try {
    const savedData = localStorage.getItem("quantum_subscription_data");
    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // Check if data is not older than 1 hour
      const ONE_HOUR = 60 * 60 * 1000;
      if (Date.now() - parsedData.timestamp < ONE_HOUR) {
        dispatch(setSelectedPackage(parsedData.selectedPackage));
        dispatch(setUserCount(parsedData.userCount));
        dispatch(setBillingType(parsedData.billingType));
        dispatch(setAppliedDiscount(parsedData.appliedDiscount));
        dispatch(setCalculatedPricing(parsedData.calculatedPricing));
        return true;
      } else {
        localStorage.removeItem("quantum_subscription_data");
      }
    }
  } catch (error) {
    console.warn("Failed to load subscription data from localStorage:", error);
    localStorage.removeItem("quantum_subscription_data");
  }
  return false;
};

export const clearLocalStorage = () => {
  try {
    localStorage.removeItem("quantum_subscription_data");
  } catch (error) {
    console.warn("Failed to clear subscription data from localStorage:", error);
  }
};

// My Subscription Management Actions
export const fetchMySubscription = () => async (dispatch) => {
  try {
    dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_START });

    const response = await api.getMySubscription();

    if (response.data.success) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_SUCCESS,
        payload: response.data.data.subscription,
      });
      return { success: true, data: response.data.data.subscription };
    } else {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_ERROR,
        payload: response.data.message || "Failed to fetch subscription",
      });
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    const message =
      error.response?.data?.message || "Failed to fetch subscription data";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

export const cancelSubscription =
  (subscriptionId, cancellationData) => async (dispatch) => {
    try {
      dispatch({ type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_START });

      const response = await api.cancelSubscription(
        subscriptionId,
        cancellationData
      );

      if (response.data.success) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_SUCCESS,
          payload: response.data.data.subscription,
        });

        // Refresh subscription data after cancellation
        dispatch(fetchMySubscription());

        return { success: true, data: response.data.data };
      } else {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_ERROR,
          payload: response.data.message || "Failed to cancel subscription",
        });
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel subscription";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_ERROR,
        payload: message,
      });
      return { success: false, message };
    }
  };

export const fetchBillingHistory = () => async (dispatch) => {
  try {
    dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_START });

    const response = await api.getMySubscription(); // Uses same endpoint as it contains payment history

    if (response.data.success && response.data.data.subscription) {
      const subscription = response.data.data.subscription;
      const billingData = {
        subscription: subscription,
        paymentHistory: subscription.paymentHistory || [],
      };

      dispatch({
        type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_SUCCESS,
        payload: billingData,
      });
      return { success: true, data: billingData };
    } else {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_ERROR,
        payload: response.data.message || "Failed to fetch billing data",
      });
      return { success: false, message: response.data.message };
    }
  } catch (error) {
    const message =
      error.response?.data?.message || "Failed to fetch billing data";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

// Plan Management Actions (following project standards pattern)
export const fetchAvailablePlans =
  (currentPackageId, openSnackbar) => async (dispatch) => {
    try {
      dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_AVAILABLE_PLANS_START });
      const { data } = await api.getAvailablePlans(currentPackageId);

      if (data.success) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.FETCH_AVAILABLE_PLANS_SUCCESS,
          payload: data.data,
        });
        return { success: true, data: data.data };
      } else {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.FETCH_AVAILABLE_PLANS_ERROR,
          payload: data.message || "Failed to fetch available plans",
        });
        if (openSnackbar) {
          openSnackbar(data.message || "Failed to fetch available plans", SNACKBAR_DURATION_MS, getSnackbarColor("data.message Failed to fetch available plans"));
        }
        return { success: false, message: data.message };
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Error fetching available plans.";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.FETCH_AVAILABLE_PLANS_ERROR,
        payload: errorMessage,
      });
      if (openSnackbar) {
        openSnackbar(errorMessage, SNACKBAR_DURATION_MS, getSnackbarColor("errorMessage"));
      }
      return { success: false, message: errorMessage };
    }
  };

// Create payment intent for plan change
export const createPlanChangePaymentIntent =
  (subscriptionId, changeData, openSnackbar) => async (dispatch) => {
    try {
      dispatch({ type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_START });
      const { data } = await api.createPlanChangePaymentIntent(
        subscriptionId,
        changeData
      );

      if (data.success) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_SUCCESS,
          payload: data.data,
        });
        return { success: true, data: data.data };
      } else {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_ERROR,
          payload: data.message || "Failed to create payment intent",
        });
        if (openSnackbar) {
          openSnackbar(data.message || "Failed to create payment intent", SNACKBAR_DURATION_MS, getSnackbarColor("data.message Failed to create payment intent"));
        }
        return { success: false, message: data.message };
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Error creating payment intent.";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_ERROR,
        payload: errorMessage,
      });
      if (openSnackbar) {
        openSnackbar(errorMessage, SNACKBAR_DURATION_MS, getSnackbarColor("errorMessage"));
      }
      return { success: false, message: errorMessage };
    }
  };

export const changePlan =
  (subscriptionId, changeData, openSnackbar) => async (dispatch) => {
    try {
      dispatch({ type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_START });
      const { data } = await api.changePlan(subscriptionId, changeData);

      if (data.success) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_SUCCESS,
          payload: data.data,
        });
        if (openSnackbar) {
          openSnackbar("Plan changed successfully.", SNACKBAR_DURATION_MS, getSnackbarColor("Plan changed successfully."));
        }

        // Refresh subscription data after plan change
        dispatch(fetchMySubscription());

        return { success: true, data: data.data };
      } else {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_ERROR,
          payload: data.message || "Failed to change plan",
        });
        if (openSnackbar) {
          openSnackbar(data.message || "Failed to change plan", SNACKBAR_DURATION_MS, getSnackbarColor("data.message Failed to change plan"));
        }
        return { success: false, message: data.message };
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Error changing plan.";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.CHANGE_PLAN_ERROR,
        payload: errorMessage,
      });
      if (openSnackbar) {
        openSnackbar(errorMessage, SNACKBAR_DURATION_MS, getSnackbarColor("errorMessage"));
      }
      return { success: false, message: errorMessage };
    }
  };

export const updateSubscriptionUserCount =
  (subscriptionId, userCountData, openSnackbar) => async (dispatch) => {
    try {
      dispatch({ type: SUBSCRIPTION_ACTIONS.UPDATE_USER_COUNT_START });
      const { data } = await api.updateUserCount(subscriptionId, userCountData);

      if (data.success) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.UPDATE_USER_COUNT_SUCCESS,
          payload: data.data,
        });
        if (openSnackbar) {
          openSnackbar("User count updated successfully.", SNACKBAR_DURATION_MS, getSnackbarColor("User count updated successfully."));
        }

        // Refresh subscription data after user count update
        dispatch(fetchMySubscription());

        return { success: true, data: data.data };
      } else {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.UPDATE_USER_COUNT_ERROR,
          payload: data.message || "Failed to update user count",
        });
        if (openSnackbar) {
          openSnackbar(data.message || "Failed to update user count", SNACKBAR_DURATION_MS, getSnackbarColor("data.message Failed to update user count"));
        }
        return { success: false, message: data.message };
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Error updating user count.";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.UPDATE_USER_COUNT_ERROR,
        payload: errorMessage,
      });
      if (openSnackbar) {
        openSnackbar(errorMessage, SNACKBAR_DURATION_MS, getSnackbarColor("errorMessage"));
      }
      return { success: false, message: errorMessage };
    }
  };

export const previewPlanChange =
  (subscriptionId, changeData, openSnackbar) => async (dispatch) => {
    try {
      dispatch({ type: SUBSCRIPTION_ACTIONS.PREVIEW_PLAN_CHANGE_START });
      const { data } = await api.previewPlanChange(subscriptionId, changeData);

      if (data.success) {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.PREVIEW_PLAN_CHANGE_SUCCESS,
          payload: data.data,
        });
        return { success: true, data: data.data };
      } else {
        dispatch({
          type: SUBSCRIPTION_ACTIONS.PREVIEW_PLAN_CHANGE_ERROR,
          payload: data.message || "Failed to preview plan change",
        });
        if (openSnackbar) {
          openSnackbar(data.message || "Failed to preview plan change", SNACKBAR_DURATION_MS, getSnackbarColor("data.message Failed to preview plan change"));
        }
        return { success: false, message: data.message };
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Error previewing plan change.";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.PREVIEW_PLAN_CHANGE_ERROR,
        payload: errorMessage,
      });
      if (openSnackbar) {
        openSnackbar(errorMessage, SNACKBAR_DURATION_MS, getSnackbarColor("errorMessage"));
      }
      return { success: false, message: errorMessage };
    }
  };

/**
 * Check if user has an active subscription
 * Following project standards for Redux actions
 */
export const checkSubscriptionStatus = (openSnackbar) => async (dispatch) => {
  try {
    // 1. Start loading
    dispatch({ type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_START });
    
    // 2. API call
    const { data } = await api.getMySubscription();
    
    // 3. Handle success — ERP returns { success, data: { hasActiveSubscription, status, subscription } }
    if (data.success) {
      const hasActiveSubscription = Boolean(data.data?.hasActiveSubscription);
      const status = data.data?.status || null;
      const subscription = data.data?.subscription || null;

      dispatch({
        type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS,
        payload: {
          hasSubscription: hasActiveSubscription,
          subscription,
          status,
        },
      });

      return {
        success: true,
        hasSubscription: hasActiveSubscription,
        subscription,
      };
    } else {
      // 4. Handle API error response - no subscription found
      dispatch({ 
        type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS, 
        payload: {
          hasSubscription: false,
          subscription: null,
          status: null
        }
      });
      
      return { 
        success: true, 
        hasSubscription: false,
        message: data.message || "No active subscription found" 
      };
    }
  } catch (error) {
    // 5. Handle network/system errors
    console.log("Subscription check error:", error);
    
    // If it's a 404 or similar, user likely has no subscription
    if (error?.response?.status === 404) {
      dispatch({ 
        type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS, 
        payload: {
          hasSubscription: false,
          subscription: null,
          status: null
        }
      });
      
      return { 
        success: true, 
        hasSubscription: false,
        message: "No subscription found" 
      };
    }
    
    // For other errors, treat as actual errors
    const errorMessage = error?.response?.data?.message || "Error checking subscription status.";
    dispatch({ 
      type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_ERROR, 
      payload: errorMessage 
    });
    
    if (openSnackbar) {
      openSnackbar(errorMessage, SNACKBAR_DURATION_MS, getSnackbarColor("errorMessage"));
    }
    
    return { success: false, message: errorMessage };
  }
};
